import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  calculateEnrollmentPremium,
  type EnrollmentCalculationResult
} from "@/lib/enrollmentCalculator";
import type {
  PlanWithCarrier,
  Employee,
  Dependent
} from "@shared/schema";
// DEV-262: data loading goes through the shared loader (contributions + rates
// incl. referenced buy-up base plans) instead of this hook's own two queries.
import { usePlanPremiumData } from "@/hooks/usePlanPremiumData";
import { toYmdLocal } from "@/lib/utils";
import { ratingAreaGapMessage } from "@shared/utils/ratingAreaCoverage";

export type PlanPremiumBreakdown = {
  plan: PlanWithCarrier;
  coverageType: string;
  totalMonthlyPremium: number;
  totalAnnualPremium: number;
  employeePortion: number;
  employerPortion: number;
  enrollmentCount: number;
  enrollments: Array<{
    employee: Employee;
    dependents: Dependent[];
    calculation: EnrollmentCalculationResult;
  }>;
};

export type QuotePremiumSummary = {
  planBreakdowns: PlanPremiumBreakdown[];
  totalMonthlyPremium: number;
  totalAnnualPremium: number;
  totalEmployeePortion: number;
  totalEmployerPortion: number;
  byCoverageType: Record<string, {
    coverageType: string;
    totalMonthly: number;
    totalAnnual: number;
    planCount: number;
    minMonthly: number;
    maxMonthly: number;
    primaryPlanMonthly: number;
  }>;
  estimatedMonthlyPremium: number;
  estimatedAnnualPremium: number;
  estimatedEmployeePortion: number;
  estimatedEmployerPortion: number;
  premiumRangeMin: number;
  premiumRangeMax: number;
  loading: boolean;
  error: string | null;
  /** DEV-382: selected plans left out of the totals because they have no
   * rates for this quote (e.g. none for the group's rating area). A quote
   * must not go out while this is non-empty - the totals would be short. */
  unpricedPlans?: Array<{ planId: number; planName: string; reason: string }>;
};

type UseQuotePremiumCalculationProps = {
  selectedPlans: Record<string, PlanWithCarrier[]>; // plans by coverage type
  selectedEmployees: number[];
  selectedDependents: number[];
  employees: Employee[];
  dependents: Dependent[];
  effectiveDate?: Date | null;
  clientId?: number | null;
  /** Confirmed rating region for the quote (e.g. CA 1..19). */
  ratingAreaId?: number | null;
  enabled?: boolean;
};

export function useQuotePremiumCalculation({
  selectedPlans,
  selectedEmployees,
  selectedDependents,
  employees,
  dependents,
  effectiveDate,
  clientId,
  ratingAreaId,
  enabled = true
}: UseQuotePremiumCalculationProps): QuotePremiumSummary {

  // Get all unique plan IDs for fetching rates
  const allPlanIds = useMemo(() => {
    const ids = new Set<number>();
    Object.values(selectedPlans).forEach(planArray => {
      planArray.forEach(plan => ids.add(plan.id));
    });
    return Array.from(ids);
  }, [selectedPlans]);

  // DEV-262: one shared loader replaces the previous contributions query +
  // referencedBasePlanIds memo + per-plan rate fetches. It returns the full
  // contributions table and the rates for the selected plans PLUS their
  // referenced buy-up base plans (whose rates × percentages define the
  // employer credit). asOf = the quote's effective date (the prospective
  // PLAN-YEAR start), so the server resolves the rate version in force for
  // that date (spec scenario 17). Passing an empty plan list when disabled
  // keeps the underlying query off, mirroring the old `enabled` flags.
  const asOfParam = toYmdLocal(effectiveDate);
  const {
    allPlanContributions,
    allPlanRates,
    ratingAreaGapsByPlanId,
    isLoading: premiumDataLoading,
  } = usePlanPremiumData(enabled ? allPlanIds : [], clientId, asOfParam, ratingAreaId ?? null);

  // Bulk-resolve each selected employee's eligibility group per plan so we
  // can scope contributions to the right rule set — one round trip for all
  // plans. Skipped for prospect quotes (no clientId): falls back to unfiltered
  // contributions, preserving today's single-group behavior.
  type BulkResolution = { groupId: number; ruleSetId: number } | null;
  const { data: resolutionsByPlan = {}, isLoading: resolutionsLoading } = useQuery<Record<number, Record<number, BulkResolution>>>({
    queryKey: ["/api/eligibility-groups/resolve-bulk", { clientId, planIds: allPlanIds, employeeIds: selectedEmployees }],
    enabled: enabled && allPlanIds.length > 0 && clientId != null && selectedEmployees.length > 0,
    queryFn: async () => {
      const resp = await fetch("/api/eligibility-groups/resolve-bulk", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ clientId, planIds: allPlanIds, employeeIds: selectedEmployees }),
      });
      if (!resp.ok) return {};
      const data = await resp.json();
      return (data.resolutionsByPlan ?? {}) as Record<number, Record<number, BulkResolution>>;
    },
  });

  // Filter employees and dependents based on selections
  const filteredEmployees = useMemo(() => {
    return employees.filter(emp => selectedEmployees.includes(emp.id));
  }, [employees, selectedEmployees]);

  const filteredDependents = useMemo(() => {
    return dependents.filter(dep => selectedDependents.includes(dep.id));
  }, [dependents, selectedDependents]);

  // Build dependent map by employee (sorted alphabetically)
  const dependentsByEmployee = useMemo(() => {
    const map = new Map<number, Dependent[]>();
    filteredDependents.forEach(dep => {
      const empId = dep.employeeId;
      if (!map.has(empId)) {
        map.set(empId, []);
      }
      map.get(empId)!.push(dep);
    });
    // Sort dependents alphabetically by last name, then first name
    map.forEach((deps, empId) => {
      deps.sort((a, b) => {
        const lastA = (a.lastName || '').toLowerCase();
        const lastB = (b.lastName || '').toLowerCase();
        if (lastA !== lastB) return lastA.localeCompare(lastB);
        const firstA = (a.firstName || '').toLowerCase();
        const firstB = (b.firstName || '').toLowerCase();
        return firstA.localeCompare(firstB);
      });
    });
    return map;
  }, [filteredDependents]);

  // Calculate premiums for all plans
  const premiumSummary = useMemo((): QuotePremiumSummary => {
    const emptyResult: QuotePremiumSummary = {
      planBreakdowns: [],
      totalMonthlyPremium: 0,
      totalAnnualPremium: 0,
      totalEmployeePortion: 0,
      totalEmployerPortion: 0,
      byCoverageType: {},
      estimatedMonthlyPremium: 0,
      estimatedAnnualPremium: 0,
      estimatedEmployeePortion: 0,
      estimatedEmployerPortion: 0,
      premiumRangeMin: 0,
      premiumRangeMax: 0,
      loading: false,
      error: null,
      unpricedPlans: []
    };

    if (!enabled || premiumDataLoading || resolutionsLoading) {
      return { ...emptyResult, loading: true };
    }

    if (filteredEmployees.length === 0) {
      return { ...emptyResult, error: "No employees selected" };
    }

    try {
      const planBreakdowns: PlanPremiumBreakdown[] = [];
      const plansWithoutRates: string[] = [];
      const unpricedPlans: NonNullable<QuotePremiumSummary["unpricedPlans"]> = [];

      // Process each coverage type
      Object.entries(selectedPlans).forEach(([coverageType, plans]) => {
        plans.forEach(plan => {
          // Get rates and contributions for this plan
          const planRates = allPlanRates.filter(r => (r as any).planId === plan.id);
          const planContributions = allPlanContributions.filter(c => (c as any).planId === plan.id);

          if (planRates.length === 0) {
            console.warn(`No rates found for plan ${plan.id} (${plan.name})`);
            const planName = plan.name || `Plan ${plan.id}`;
            plansWithoutRates.push(planName);
            const gap = ratingAreaGapsByPlanId?.[plan.id];
            unpricedPlans.push({
              planId: plan.id,
              planName,
              reason: gap
                ? ratingAreaGapMessage(gap, planName)
                : `${planName} has no rates configured for this quote's effective date.`,
            });
            return;
          }

          let totalMonthly = 0;
          let totalEmployeePortion = 0;
          let totalEmployerPortion = 0;
          const enrollments: PlanPremiumBreakdown["enrollments"] = [];

          // Calculate premium for each employee
          filteredEmployees.forEach(employee => {
            const employeeDependents = dependentsByEmployee.get(employee.id) || [];

            try {
              // Scope contributions to this employee's resolved group rule set
              // when available; fall back to all plan contributions (prospect
              // quotes / pre-multi-group data).
              const resolution = resolutionsByPlan[plan.id]?.[employee.id];
              const employeeContributions = resolution?.ruleSetId != null
                ? planContributions.filter(
                    c => Number((c as any).contributionRuleSetId) === resolution.ruleSetId
                  )
                : planContributions;

              const calculation = calculateEnrollmentPremium({
                plan: plan as any,
                planRates,
                planContributions: employeeContributions,
                employee,
                dependents: employeeDependents,
                ageAsOf: effectiveDate || undefined,
                allPlanContributions,
                allPlanRates,
              });

              totalMonthly += calculation.totalMonthlyRate;
              totalEmployeePortion += calculation.employeePremium;
              totalEmployerPortion += calculation.employerPremium;

              enrollments.push({
                employee,
                dependents: employeeDependents,
                calculation
              });
            } catch (error) {
              console.error(`Failed to calculate premium for employee ${employee.id} (${employee.firstName} ${employee.lastName}):`, error);
              console.error('Employee data:', {
                id: employee.id,
                name: `${employee.firstName} ${employee.lastName}`,
                dateOfBirth: employee.dateOfBirth,
                hasRates: planRates.length,
                planName: plan.name
              });
            }
          });

          planBreakdowns.push({
            plan,
            coverageType,
            totalMonthlyPremium: totalMonthly,
            totalAnnualPremium: totalMonthly * 12,
            employeePortion: totalEmployeePortion,
            employerPortion: totalEmployerPortion,
            enrollmentCount: enrollments.length,
            enrollments
          });
        });
      });

      // Calculate totals (sum of ALL plans - kept for backward compatibility but represents all options, not realistic total)
      const totalMonthlyPremium = planBreakdowns.reduce((sum, pb) => sum + pb.totalMonthlyPremium, 0);
      const totalAnnualPremium = totalMonthlyPremium * 12;
      const totalEmployeePortion = planBreakdowns.reduce((sum, pb) => sum + pb.employeePortion, 0);
      const totalEmployerPortion = planBreakdowns.reduce((sum, pb) => sum + pb.employerPortion, 0);

      // Calculate by coverage type with ranges
      // Plans within same coverage type are ALTERNATIVES - client picks ONE
      const byCoverageType: QuotePremiumSummary["byCoverageType"] = {};
      planBreakdowns.forEach(pb => {
        if (!byCoverageType[pb.coverageType]) {
          byCoverageType[pb.coverageType] = {
            coverageType: pb.coverageType,
            totalMonthly: pb.totalMonthlyPremium, // Will be replaced with sum for display
            totalAnnual: pb.totalAnnualPremium,
            planCount: 0,
            minMonthly: pb.totalMonthlyPremium,
            maxMonthly: pb.totalMonthlyPremium,
            primaryPlanMonthly: pb.totalMonthlyPremium // First plan is primary
          };
        } else {
          // Track min/max for range display
          byCoverageType[pb.coverageType].minMonthly = Math.min(
            byCoverageType[pb.coverageType].minMonthly,
            pb.totalMonthlyPremium
          );
          byCoverageType[pb.coverageType].maxMonthly = Math.max(
            byCoverageType[pb.coverageType].maxMonthly,
            pb.totalMonthlyPremium
          );
        }
        byCoverageType[pb.coverageType].totalMonthly += pb.totalMonthlyPremium;
        byCoverageType[pb.coverageType].totalAnnual += pb.totalAnnualPremium;
        byCoverageType[pb.coverageType].planCount += 1;
      });

      // Calculate ESTIMATED totals based on selecting ONE plan (the first/primary) per coverage type
      // This represents a realistic scenario where client picks one plan per coverage
      let estimatedMonthlyPremium = 0;
      let estimatedEmployeePortion = 0;
      let estimatedEmployerPortion = 0;
      let premiumRangeMin = 0;
      let premiumRangeMax = 0;

      // Group breakdowns by coverage type to get first plan of each
      const breakdownsByCoverage: Record<string, PlanPremiumBreakdown[]> = {};
      planBreakdowns.forEach(pb => {
        if (!breakdownsByCoverage[pb.coverageType]) {
          breakdownsByCoverage[pb.coverageType] = [];
        }
        breakdownsByCoverage[pb.coverageType].push(pb);
      });

      // Sum primary plan costs (first plan of each coverage type) for estimated total
      // Also calculate min/max range across all combinations
      Object.values(breakdownsByCoverage).forEach(coveragePlans => {
        if (coveragePlans.length > 0) {
          // Primary = first plan
          const primaryPlan = coveragePlans[0];
          estimatedMonthlyPremium += primaryPlan.totalMonthlyPremium;
          estimatedEmployeePortion += primaryPlan.employeePortion;
          estimatedEmployerPortion += primaryPlan.employerPortion;

          // Find min and max for this coverage type
          const minPlan = coveragePlans.reduce((min, p) => 
            p.totalMonthlyPremium < min.totalMonthlyPremium ? p : min, coveragePlans[0]);
          const maxPlan = coveragePlans.reduce((max, p) => 
            p.totalMonthlyPremium > max.totalMonthlyPremium ? p : max, coveragePlans[0]);

          premiumRangeMin += minPlan.totalMonthlyPremium;
          premiumRangeMax += maxPlan.totalMonthlyPremium;
        }
      });

      const estimatedAnnualPremium = estimatedMonthlyPremium * 12;

      // If we have plans selected but no breakdowns, it means plans don't have rates configured
      if (planBreakdowns.length === 0 && plansWithoutRates.length > 0) {
        const areaGaps = unpricedPlans.filter((u) => ratingAreaGapsByPlanId?.[u.planId]);
        return {
          ...emptyResult,
          unpricedPlans,
          error: areaGaps.length > 0
            ? areaGaps.map((u) => u.reason).join(" ")
            : `No rates configured for: ${plansWithoutRates.join(", ")}. Please add rates for these plans in the Plans management section.`
        };
      }

      return {
        planBreakdowns,
        totalMonthlyPremium,
        totalAnnualPremium,
        totalEmployeePortion,
        totalEmployerPortion,
        byCoverageType,
        estimatedMonthlyPremium,
        estimatedAnnualPremium,
        estimatedEmployeePortion,
        estimatedEmployerPortion,
        premiumRangeMin,
        premiumRangeMax,
        loading: false,
        error: null,
        unpricedPlans
      };
    } catch (error) {
      console.error("Error calculating quote premiums:", error);
      return {
        ...emptyResult,
        error: error instanceof Error ? error.message : "Failed to calculate premiums"
      };
    }
  }, [
    enabled,
    premiumDataLoading,
    resolutionsLoading,
    resolutionsByPlan,
    selectedPlans,
    filteredEmployees,
    dependentsByEmployee,
    allPlanRates,
    allPlanContributions,
    ratingAreaGapsByPlanId,
    effectiveDate
  ]);

  return premiumSummary;
}
